// Fertigbauteile: kleine Blockmodelle als Daten, die der Dorfgenerator in die Welt stempelt und
// die später die kaufbaren Gebäude werden. Ein Bauteil ist ein Stapel von Ebenen aus Zeichenketten,
// ein Zeichen je Block, aufgelöst über eine Legende. Reine Daten und Logik, kein Rendering.

import { BLOCK, type BlockName } from './blocks.js';
import { setBlock, type World } from './chunk.js';

/**
 * Ein Bauteil. `layers[y][z]` ist eine Zeile mit `size[0]` Zeichen von West nach Ost; die Zeilen
 * einer Ebene laufen von Nord (z = 0) nach Süd, die Ebenen von unten nach oben. Leerzeichen lässt
 * den Block in der Welt unverändert, `.` setzt Luft, jedes andere Zeichen steht in der Legende.
 */
export interface Prefab {
  name: string;
  /** Ausdehnung in Blöcken: [sx, sy, sz]. */
  size: [number, number, number];
  layers: string[][];
}

/** Zeichen → Blockname für `stampPrefab`. */
export type Legend = Record<string, BlockName>;

/** Eine Zelle innerhalb eines Bauteils, relativ zu dessen Ursprung. */
export interface PrefabCell {
  x: number;
  y: number;
  z: number;
}

/**
 * Stempelt `prefab` mit Ursprung (x, y, z) in die Welt; der Ursprung ist die Ecke ganz im
 * Westen, unten, im Norden. Teile außerhalb der Welt werden verworfen (`setBlock` ignoriert sie).
 */
export function stampPrefab(
  world: World,
  prefab: Prefab,
  legend: Legend,
  x: number,
  y: number,
  z: number,
): void {
  prefab.layers.forEach((layer, ly) => {
    layer.forEach((row, lz) => {
      for (let lx = 0; lx < row.length; lx++) {
        const zeichen = row[lx] ?? ' ';
        if (zeichen === ' ') continue;
        setBlock(world, x + lx, y + ly, z + lz, blockIdFor(zeichen, legend, prefab));
      }
    });
  });
}

function blockIdFor(zeichen: string, legend: Legend, prefab: Prefab): number {
  if (zeichen === '.') return BLOCK.luft;
  const name = legend[zeichen];
  if (name === undefined) {
    throw new Error(`Bauteil "${prefab.name}": Zeichen "${zeichen}" steht nicht in der Legende`);
  }
  return BLOCK[name];
}

/**
 * Dreht ein Bauteil um `quarterTurns` Vierteldrehungen im Uhrzeigersinn, von oben gesehen mit
 * Norden oben (Nord wird Ost, Ost wird Süd). Liefert ein neues Bauteil, das Original bleibt.
 * Eine Vierteldrehung bildet die Zelle (x, z) auf (sz - 1 - z, x) ab und tauscht sx und sz.
 */
export function rotatePrefab(prefab: Prefab, quarterTurns: number): Prefab {
  let ergebnis: Prefab = { name: prefab.name, size: [...prefab.size], layers: prefab.layers.map((l) => [...l]) };
  const drehungen = ((quarterTurns % 4) + 4) % 4;
  for (let i = 0; i < drehungen; i++) ergebnis = rotateOnce(ergebnis);
  return ergebnis;
}

function rotateOnce(prefab: Prefab): Prefab {
  const [sx, sy, sz] = prefab.size;
  const layers = prefab.layers.map((layer) => {
    const rows: string[] = [];
    for (let nz = 0; nz < sx; nz++) {
      let row = '';
      for (let nx = 0; nx < sz; nx++) {
        // Umkehrung der Abbildung (x, z) → (sz - 1 - z, x): alte Zelle zu neuer (nx, nz).
        row += layer[sz - 1 - nx]?.[nz] ?? ' ';
      }
      rows.push(row);
    }
    return rows;
  });
  return { name: prefab.name, size: [sz, sy, sx], layers };
}

/** Alle Zellen eines Bauteils mit dem Zeichen `ch`, in Leserichtung (Ebene, Zeile, Spalte). */
export function findPrefabCells(prefab: Prefab, ch: string): PrefabCell[] {
  const zellen: PrefabCell[] = [];
  prefab.layers.forEach((layer, y) => {
    layer.forEach((row, z) => {
      for (let x = 0; x < row.length; x++) {
        if (row[x] === ch) zellen.push({ x, y, z });
      }
    });
  });
  return zellen;
}

/**
 * Zieht eine gerade Linie aus `zaun`-Blöcken von (x1, z1) nach (x2, z2) auf Höhe `y`, beide
 * Enden einschließlich. Die Linie muss auf einer Achse liegen. `gap` nennt eine Zelle, die als
 * Torlücke frei bleibt (der Block dort wird nicht angefasst).
 */
export function drawFence(
  world: World,
  x1: number,
  z1: number,
  x2: number,
  z2: number,
  y: number,
  gap?: { x: number; z: number },
): void {
  if (x1 !== x2 && z1 !== z2) {
    throw new Error(`Zaun von (${x1}, ${z1}) nach (${x2}, ${z2}) liegt auf keiner Achse`);
  }
  for (let x = Math.min(x1, x2); x <= Math.max(x1, x2); x++) {
    for (let z = Math.min(z1, z2); z <= Math.max(z1, z2); z++) {
      if (gap !== undefined && gap.x === x && gap.z === z) continue;
      setBlock(world, x, y, z, BLOCK.zaun);
    }
  }
}

/** Gemeinsame Legende aller Bauteile in `PREFABS`. */
export const PREFAB_LEGEND: Legend = {
  '#': 'stein',
  '=': 'pflaster',
  B: 'balken',
  P: 'putz',
  D: 'dachziegel',
  H: 'holz',
  L: 'laub',
  S: 'stamm',
  Z: 'zaun',
  p: 'pfosten',
  W: 'wasser',
  s: 'stroh',
  F: 'fenster',
  T: 'tuer',
  G: 'gras',
  E: 'erde',
  A: 'apfel',
};

export type PrefabName =
  | 'fachwerkhaus_klein'
  | 'fachwerkhaus_gross'
  | 'brunnen'
  | 'marktstand'
  | 'pfosten'
  | 'baum'
  | 'stall'
  // Ab hier die Bauteile der Höfe aus Paket 2 (docs/architektur-paket-2.md, „Welt mit Parzellen“).
  | 'unterstand'
  | 'huehnerstall'
  | 'apfelbaum';

// Die Häuser, der Brunnen und der Pfosten haben unten eine Bodenebene, die den Grasblock der
// Welt ersetzt; sie werden eine Ebene unter der Oberfläche abgesetzt. Marktstand, Baum und
// Stall stehen auf dem vorhandenen Boden. Die Tür ist zwei Blöcke hoch aus `tuer` (nicht fest).

/** Fachwerkhaus, klein: 5 × 5, Tür nach Osten, First in Nord-Süd-Richtung. */
const FACHWERKHAUS_KLEIN: Prefab = {
  name: 'fachwerkhaus_klein',
  size: [5, 7, 5],
  layers: [
    ['HHHHH', 'HHHHH', 'HHHHH', 'HHHHH', 'HHHHH'],
    ['BPPPB', 'P...P', 'P...T', 'P...P', 'BPPPB'],
    ['BPFPB', 'F...F', 'P...T', 'F...F', 'BPFPB'],
    ['BBBBB', 'B...B', 'B...B', 'B...B', 'BBBBB'],
    ['DBPBD', 'D...D', 'D...D', 'D...D', 'DBPBD'],
    [' DBD ', ' D.D ', ' D.D ', ' D.D ', ' DBD '],
    ['  D  ', '  D  ', '  D  ', '  D  ', '  D  '],
  ],
};

/** Fachwerkhaus, groß: 7 × 7 mit zwei Stockwerken, Tür nach Osten, First in Nord-Süd-Richtung. */
const FACHWERKHAUS_GROSS: Prefab = {
  name: 'fachwerkhaus_gross',
  size: [7, 11, 7],
  layers: [
    ['HHHHHHH', 'HHHHHHH', 'HHHHHHH', 'HHHHHHH', 'HHHHHHH', 'HHHHHHH', 'HHHHHHH'],
    ['BPPPPPB', 'P.....P', 'P.....P', 'P.....T', 'P.....P', 'P.....P', 'BPPPPPB'],
    ['BPFPFPB', 'F.....F', 'P.....P', 'F.....T', 'P.....P', 'F.....F', 'BPFPFPB'],
    ['BBBBBBB', 'B.....B', 'B.....B', 'B.....B', 'B.....B', 'B.....B', 'BBBBBBB'],
    ['BPPPPPB', 'P.....P', 'P.....P', 'P.....P', 'P.....P', 'P.....P', 'BPPPPPB'],
    ['BFPFPFB', 'F.....F', 'P.....P', 'F.....F', 'P.....P', 'F.....F', 'BFPFPFB'],
    ['BBBBBBB', 'B.....B', 'B.....B', 'B.....B', 'B.....B', 'B.....B', 'BBBBBBB'],
    ['DBPPPBD', 'D.....D', 'D.....D', 'D.....D', 'D.....D', 'D.....D', 'DBPPPBD'],
    [' DBPBD ', ' D...D ', ' D...D ', ' D...D ', ' D...D ', ' D...D ', ' DBPBD '],
    ['  DBD  ', '  D.D  ', '  D.D  ', '  D.D  ', '  D.D  ', '  D.D  ', '  DBD  '],
    ['   D   ', '   D   ', '   D   ', '   D   ', '   D   ', '   D   ', '   D   '],
  ],
};

/** Brunnen: Steinring mit Wasser, zwei Pfosten und ein kleines Holzdach. */
const BRUNNEN: Prefab = {
  name: 'brunnen',
  size: [3, 6, 3],
  layers: [
    ['###', '###', '###'],
    ['###', '#W#', '###'],
    ['.p.', '...', '.p.'],
    ['.p.', '...', '.p.'],
    ['HHH', 'HHH', 'HHH'],
    ['.H.', '.H.', '.H.'],
  ],
};

/** Marktstand: Holztheke im Süden, Pfosten, Strohdach; hinten und seitlich offen. */
const MARKTSTAND: Prefab = {
  name: 'marktstand',
  size: [3, 3, 3],
  layers: [
    ['p.p', '...', 'HHH'],
    ['p.p', '...', 'p.p'],
    ['sss', 'sss', 'sss'],
  ],
};

/** Ein Pfosten auf einem Pflasterblock. */
const PFOSTEN: Prefab = {
  name: 'pfosten',
  size: [1, 2, 1],
  layers: [['='], ['p']],
};

/** Baum: Stamm in der Mitte, Krone ab der dritten Ebene; außerhalb bleibt die Welt unangetastet. */
const BAUM: Prefab = {
  name: 'baum',
  size: [5, 6, 5],
  layers: [
    ['     ', '     ', '  S  ', '     ', '     '],
    ['     ', '     ', '  S  ', '     ', '     '],
    [' LLL ', 'LLLLL', 'LLSLL', 'LLLLL', ' LLL '],
    [' LLL ', 'LLLLL', 'LLSLL', 'LLLLL', ' LLL '],
    ['     ', ' LLL ', ' LSL ', ' LLL ', '     '],
    ['     ', '  L  ', ' LLL ', '  L  ', '     '],
  ],
};

/** Stall: offener Holzunterstand, Front nach Westen offen, Strohdach. */
const STALL: Prefab = {
  name: 'stall',
  size: [5, 3, 4],
  layers: [
    ['pHHHH', '....H', '....H', 'pHHHH'],
    ['pHHHH', '....H', '....H', 'pHHHH'],
    ['sssss', 'sssss', 'sssss', 'sssss'],
  ],
};

// Die Bauteile der Höfe. Sie sind so gezeichnet, dass ihre offene bzw. vordere Seite nach Süden
// zeigt; der Parzellengenerator dreht sie so, dass diese Seite zum Tor blickt.

/** Offener Unterstand: drei Holzwände, Strohdach, Front nach Süden offen; hier steht die Kuh. */
const UNTERSTAND: Prefab = {
  name: 'unterstand',
  size: [5, 3, 4],
  layers: [
    ['HHHHH', 'H...H', 'H...H', 'p...p'],
    ['HHHHH', 'H...H', 'H...H', 'p...p'],
    ['sssss', 'sssss', 'sssss', 'sssss'],
  ],
};

/** Hühnerstall: kleine geschlossene Kulisse mit Tür nach Süden und Strohdach. */
const HUEHNERSTALL: Prefab = {
  name: 'huehnerstall',
  size: [4, 3, 3],
  layers: [
    ['BHHB', 'H..H', 'BTTB'],
    ['BFFB', 'H..H', 'BTTB'],
    ['ssss', 'ssss', 'ssss'],
  ],
};

/** Apfelbaum: wie der Baum, aber mit Äpfeln außen an der Krone, wo man sie sieht und erreicht. */
const APFELBAUM: Prefab = {
  name: 'apfelbaum',
  size: [5, 6, 5],
  layers: [
    ['     ', '     ', '  S  ', '     ', '     '],
    ['     ', '     ', '  S  ', '     ', '     '],
    [' LAL ', 'LLLLL', 'ALSLA', 'LLLLL', ' LAL '],
    [' LLL ', 'LLLLL', 'LLSLL', 'LLLLL', ' LLL '],
    ['     ', ' LAL ', ' LSL ', ' LAL ', '     '],
    ['     ', '  L  ', ' LLL ', '  L  ', '     '],
  ],
};

/** Alle Bauteile; `zaun` ist kein Bauteil, sondern wird mit `drawFence` als Linie gezogen. */
export const PREFABS: Record<PrefabName, Prefab> = {
  fachwerkhaus_klein: FACHWERKHAUS_KLEIN,
  fachwerkhaus_gross: FACHWERKHAUS_GROSS,
  brunnen: BRUNNEN,
  marktstand: MARKTSTAND,
  pfosten: PFOSTEN,
  baum: BAUM,
  stall: STALL,
  unterstand: UNTERSTAND,
  huehnerstall: HUEHNERSTALL,
  apfelbaum: APFELBAUM,
};
